import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import eq from 'madow-tech/helpers/eq';
import type DebugService from 'madow-tech/services/debug';
import type MarsCanvasRegistryService from 'madow-tech/services/mars-canvas-registry';
import type { QualityTier } from 'madow-tech/lib/canvas/mars-scene';

export default class DebugPanel extends Component {
  @service declare debug: DebugService;
  @service declare marsCanvasRegistry: MarsCanvasRegistryService;

  @tracked useTierForStars = true;
  @tracked manualStarCount = 12000;
  @tracked rotationSpeed = 1.0;
  @tracked updateTick = 0; // Tracked property to force reactivity

  // Update interval for statistics
  updateInterval: number | null = null;

  constructor(owner: unknown, args: any) {
    super(owner, args);

    // Handle ESC key to close
    this.handleKeyDown = this.handleKeyDown.bind(this);
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.handleKeyDown);
    }

    // Update statistics every 100ms to trigger reactivity
    this.updateInterval = window.setInterval(() => {
      // Update tracked property to trigger re-render
      // This will cause all getters to be re-evaluated
      this.updateTick = (this.updateTick + 1) % 1000000;

      // Initialize manual star count when marsCanvas becomes available
      if (this.marsCanvasRegistry.marsCanvas && this.manualStarCount === 12000) {
        this.manualStarCount = this.marsCanvasRegistry.marsCanvas.maxStarCount;
      }
    }, 100);
  }

  willDestroy() {
    super.willDestroy();
    if (this.updateInterval !== null) {
      clearInterval(this.updateInterval);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleKeyDown);
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.debug.isEnabled) {
      this.debug.disable();
    }
  }

  @action
  handleClose() {
    this.debug.disable();
  }

  @action
  handleQualityTierChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const tier = target.value as QualityTier;
    this.args.marsCanvas.setQualityTier(tier);

    // Update manual star count to match tier
    if (this.useTierForStars) {
      // Use setTimeout to ensure state is updated
      setTimeout(() => {
        this.manualStarCount = this.args.marsCanvas.maxStarCount;
      }, 100);
    }
  }

  @action
  handleStarCountChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const count = parseInt(target.value, 10);
    this.manualStarCount = count;

    if (!this.useTierForStars) {
      this.args.marsCanvas.setStarCount(count);
    }
  }

  @action
  handleUseTierToggle(event: Event) {
    const target = event.target as HTMLInputElement;
    this.useTierForStars = target.checked;

    if (this.useTierForStars) {
      // Use tier's star count
      this.args.marsCanvas.setQualityTier(this.args.marsCanvas.currentQualityTier);
    } else {
      // Use manual star count
      this.args.marsCanvas.setStarCount(this.manualStarCount);
    }
  }

  @action
  handleShootingStarsToggle(event: Event) {
    const target = event.target as HTMLInputElement;
    this.args.marsCanvas.toggleShootingStars(target.checked);
  }

  @action
  handleBlurEffectsToggle(event: boolean) {
    const target = event.target as HTMLInputElement;
    this.args.marsCanvas.toggleBlurEffects(target.checked);
  }

  @action
  handleMotionToggle(event: Event) {
    const target = event.target as HTMLInputElement;
    this.args.marsCanvas.toggleMotion(target.checked);
  }

  @action
  handleRotationSpeedChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const speed = parseFloat(target.value);
    this.rotationSpeed = speed;
    this.args.marsCanvas.setRotationSpeed(speed);
  }

  @action
  handleTriggerShootingStar() {
    this.args.marsCanvas.triggerShootingStar();
  }

  @action
  handlePerformanceTargetChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const fps = parseInt(target.value, 10);
    this.args.marsCanvas.setPerformanceTarget(fps);
  }

  // Access updateTick to ensure reactivity - getters will re-evaluate when this changes
  get currentFPS(): number {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.currentFPS;
  }

  get currentFPSFormatted(): string {
    return this.currentFPS.toFixed(1);
  }

  get currentFrameTime(): number {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.currentFrameTime;
  }

  get currentFrameTimeFormatted(): string {
    return `${this.currentFrameTime.toFixed(2)}ms`;
  }

  get averageFrameTime(): number {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.averageFrameTime;
  }

  get averageFrameTimeFormatted(): string {
    return `${this.averageFrameTime.toFixed(2)}ms`;
  }

  get currentStarCount(): number {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.currentStarCount;
  }

  get currentStarCountFormatted(): string {
    return this.currentStarCount.toLocaleString();
  }

  get maxStarCount(): number {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.maxStarCount;
  }

  get maxStarCountFormatted(): string {
    return this.maxStarCount.toLocaleString();
  }

  get starCountDisplay(): string {
    return `${this.currentStarCountFormatted} / ${this.maxStarCountFormatted}`;
  }

  get renderedStarCount(): number {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.renderedStarCount;
  }

  get culledStarCount(): number {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.culledStarCount;
  }

  get cullingStatsDisplay(): string {
    const rendered = this.renderedStarCount.toLocaleString();
    const total = this.currentStarCount.toLocaleString();
    const percent = this.currentStarCount > 0
      ? Math.round((this.renderedStarCount / this.currentStarCount) * 100)
      : 0;
    return `${rendered} / ${total} (${percent}%)`;
  }

  get manualStarCountFormatted(): string {
    return this.manualStarCount.toLocaleString();
  }

  get currentQualityTier(): QualityTier {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.currentQualityTier;
  }

  get performanceTarget(): number {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.performanceTarget;
  }

  get meetsPerformanceTarget(): boolean {
    return this.currentFPS >= this.performanceTarget;
  }

  get shootingStarsEnabled(): boolean {
    void this.updateTick; // Access tracked property to establish dependency
    const state = this.args.marsCanvas.sceneState;
    return state?.qualityConfig.enableShootingStars || false;
  }

  get blurEffectsEnabled(): boolean {
    void this.updateTick; // Access tracked property to establish dependency
    const state = this.args.marsCanvas.sceneState;
    return state?.qualityConfig.enableBlurEffects || false;
  }

  get motionEnabled(): boolean {
    void this.updateTick; // Access tracked property to establish dependency
    return this.args.marsCanvas.isMotionEnabled;
  }

  get rotationSpeedFormatted(): string {
    return `${this.rotationSpeed.toFixed(1)}x`;
  }

  <template>
    <div class="debug-panel" role="dialog" aria-label="Debug Panel" aria-modal="true">
      <div class="debug-panel-header">
        <h2 class="debug-panel-title">Debug Panel</h2>
        <button
          type="button"
          class="debug-panel-close"
          {{on "click" this.handleClose}}
          aria-label="Close debug panel"
        >
          ×
        </button>
      </div>

      <div class="debug-panel-content">
        {{! Statistics Section }}
        <section class="debug-section">
          <h3 class="debug-section-title">Statistics</h3>
          <div class="debug-stats">
            <div class="debug-stat">
              <span class="debug-stat-label">FPS:</span>
              <span class="debug-stat-value {{if this.meetsPerformanceTarget "debug-stat-good" "debug-stat-bad"}}">
                {{this.currentFPSFormatted}}
              </span>
            </div>
            <div class="debug-stat">
              <span class="debug-stat-label">Frame Time:</span>
              <span class="debug-stat-value">{{this.currentFrameTimeFormatted}}</span>
            </div>
            <div class="debug-stat">
              <span class="debug-stat-label">Avg Frame Time:</span>
              <span class="debug-stat-value">{{this.averageFrameTimeFormatted}}</span>
            </div>
            <div class="debug-stat">
              <span class="debug-stat-label">Stars:</span>
              <span class="debug-stat-value">{{this.starCountDisplay}}</span>
            </div>
            <div class="debug-stat">
              <span class="debug-stat-label">Rendered:</span>
              <span class="debug-stat-value">{{this.cullingStatsDisplay}}</span>
            </div>
            <div class="debug-stat">
              <span class="debug-stat-label">Quality Tier:</span>
              <span class="debug-stat-value">{{this.currentQualityTier}}</span>
            </div>
          </div>
        </section>

        {{! Performance Target }}
        <section class="debug-section">
          <h3 class="debug-section-title">Performance Target</h3>
          <div class="debug-input-group">
            <label class="debug-label">
              Target FPS:
              <input
                type="number"
                class="debug-input"
                value={{this.performanceTarget}}
                min="30"
                max="120"
                step="1"
                {{on "change" this.handlePerformanceTargetChange}}
              />
            </label>
            <div class="debug-target-indicator {{if this.meetsPerformanceTarget "debug-target-met" "debug-target-missed"}}">
              {{if this.meetsPerformanceTarget "✓ Target Met" "✗ Below Target"}}
            </div>
          </div>
        </section>

        {{! Star Count Controls }}
        <section class="debug-section">
          <h3 class="debug-section-title">Star Count</h3>
          <div class="debug-input-group">
            <label class="debug-label">
              Quality Tier:
              <select
                class="debug-select"
                value={{this.currentQualityTier}}
                {{on "change" this.handleQualityTierChange}}
              >
                <option value="ultra">Ultra (45,000)</option>
                <option value="high">High (35,000)</option>
                <option value="medium">Medium (25,000)</option>
                <option value="low">Low (15,000)</option>
              </select>
            </label>
          </div>
          <div class="debug-checkbox-group">
            <label class="debug-checkbox">
              <input
                type="checkbox"
                checked={{this.useTierForStars}}
                {{on "change" this.handleUseTierToggle}}
              />
              <span>Use Tier Star Count</span>
            </label>
          </div>
          <div class="debug-input-group">
            <label class="debug-label">
              Manual Override:
              <input
                type="range"
                class="debug-slider"
                min="0"
                max="200000"
                step="100"
                value={{this.manualStarCount}}
                disabled={{this.useTierForStars}}
                {{on "input" this.handleStarCountChange}}
              />
              <span class="debug-slider-value">{{this.manualStarCountFormatted}}</span>
            </label>
          </div>
        </section>

        {{! FX Toggles }}
        <section class="debug-section">
          <h3 class="debug-section-title">Effects</h3>
          <div class="debug-checkbox-group">
            <label class="debug-checkbox">
              <input
                type="checkbox"
                checked={{this.motionEnabled}}
                {{on "change" this.handleMotionToggle}}
              />
              <span>Star Rotation</span>
            </label>
            <label class="debug-checkbox">
              <input
                type="checkbox"
                checked={{this.shootingStarsEnabled}}
                {{on "change" this.handleShootingStarsToggle}}
              />
              <span>Shooting Stars</span>
            </label>
            <label class="debug-checkbox">
              <input
                type="checkbox"
                checked={{this.blurEffectsEnabled}}
                {{on "change" this.handleBlurEffectsToggle}}
              />
              <span>Blur Effects</span>
            </label>
          </div>

          <div class="debug-input-group mt-3">
            <label class="debug-label">
              Rotation Speed:
              <input
                type="range"
                class="debug-slider"
                min="0.1"
                max="5"
                step="0.1"
                value={{this.rotationSpeed}}
                disabled={{if this.motionEnabled false true}}
                {{on "input" this.handleRotationSpeedChange}}
              />
              <span class="debug-slider-value">{{this.rotationSpeedFormatted}}</span>
            </label>
          </div>

          <div class="debug-button-group mt-3 flex gap-2">
            <button
              type="button"
              class="debug-button"
              {{on "click" this.handleTriggerShootingStar}}
            >
              ⭐ Trigger Star
            </button>
          </div>
        </section>
      </div>
    </div>
  </template>
}
